import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PaletteIcon from '@mui/icons-material/Palette';
import PreviewIcon from '@mui/icons-material/Preview';
import {
  landingPageApi,
  LandingPageSettings,
  LeadMagnetSettings,
  LandingPageUpdatePayload,
} from '../../services/landingPageApi';
import {
  BasicInfoTab,
  LeadMagnetTab,
  AppearanceTab,
  PreviewTab,
} from '../../components/landingPage';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const LandingPageEditor = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const queryClient = useQueryClient();

  // Fetch landing page data
  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['landingPage'],
    queryFn: landingPageApi.getLandingPage,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (updates: LandingPageUpdatePayload) => landingPageApi.updateLandingPage(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landingPage'] });
      setSnackbar({
        open: true,
        message: 'Changes saved successfully!',
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to save changes',
        severity: 'error',
      });
    },
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSaveLandingPage = (updates: Partial<LandingPageSettings>) => {
    updateMutation.mutate({ landingPage: updates });
  };

  const handleSaveLeadMagnet = (updates: Partial<LeadMagnetSettings>) => {
    updateMutation.mutate({ leadMagnet: updates });
  };

  const handlePublishToggle = (isPublished: boolean) => {
    updateMutation.mutate({ landingPage: { isPublished } });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Failed to load landing page settings. Please try again later.
        </Alert>
      </Container>
    );
  }

  if (!data) {
    return null;
  }

  const { landingPage, leadMagnet, slug, publicUrl, leadStats } = data;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          Landing Page Editor
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Customize your public landing page to attract and convert potential clients.
        </Typography>
      </Box>

      {/* Lead Stats Banner */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'primary.50',
          border: '1px solid',
          borderColor: 'primary.100',
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            Leads This Month
          </Typography>
          <Typography variant="h5" fontWeight={700} color="primary.main">
            {leadStats.leadsThisMonth}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Total Leads
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {leadStats.totalLeads}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Last Lead
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {leadStats.lastLeadAt
              ? new Date(leadStats.lastLeadAt).toLocaleDateString()
              : 'No leads yet'}
          </Typography>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 64,
              textTransform: 'none',
            },
          }}
        >
          <Tab
            icon={<EditIcon />}
            iconPosition="start"
            label="Basic Info"
          />
          <Tab
            icon={<CardGiftcardIcon />}
            iconPosition="start"
            label="Lead Magnet"
          />
          <Tab
            icon={<PaletteIcon />}
            iconPosition="start"
            label="Appearance"
          />
          <Tab
            icon={<PreviewIcon />}
            iconPosition="start"
            label="Preview & Publish"
          />
        </Tabs>

        <Box sx={{ px: 3 }}>
          <TabPanel value={activeTab} index={0}>
            <BasicInfoTab
              data={landingPage}
              onSave={handleSaveLandingPage}
              isSaving={updateMutation.isPending}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <LeadMagnetTab
              data={leadMagnet}
              onSave={handleSaveLeadMagnet}
              isSaving={updateMutation.isPending}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <AppearanceTab
              data={landingPage}
              onSave={handleSaveLandingPage}
              isSaving={updateMutation.isPending}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <PreviewTab
              landingPage={landingPage}
              leadMagnet={leadMagnet}
              slug={slug}
              publicUrl={publicUrl}
              onPublishToggle={handlePublishToggle}
              isSaving={updateMutation.isPending}
            />
          </TabPanel>
        </Box>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LandingPageEditor;
